import { API_BASE } from '../../core/network/api';
import { resolveSocialIcon, socialMaskStyle } from '../ui/social-icons';

/**
 * The public site's shared frame: stylesheet, header, footer and the small
 * helpers every public page uses.
 *
 * The landing page (`/`) and the News and Guides pages (`/news`, `/guides`) are
 * all SPA routes and all draw their header and footer from here, so the three
 * cannot drift apart. They used to: News and Guides were static HTML files under
 * public/site/ with a hand-written copy of this header and footer in site.js.
 */

/** Where the site's art, fonts and stylesheet live. */
export const A = '/site/assets';

let cssLink: HTMLLinkElement | null = null;

export function loadCSS() {
  if (cssLink) return;
  cssLink = document.createElement('link');
  cssLink.rel = 'stylesheet';
  cssLink.href = `${A}/site.css`;
  document.head.appendChild(cssLink);
}

export function unloadCSS() {
  cssLink?.remove();
  cssLink = null;
}

export function cmsGet<T>(path: string): Promise<T> {
  return fetch(API_BASE + path, { credentials: 'omit' })
    .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); });
}

export function el(tag: string, cls?: string | null, text?: string | null): HTMLElement {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function mediaUrl(u: string | null | undefined): string {
  if (!u) return '';
  if (/^https?:\/\//.test(u) || u.indexOf('data:') === 0) return u;
  return (u.charAt(0) === '/' ? u : '/' + u);
}

export function safeHref(url: string | null | undefined): string {
  const u = String(url == null ? '' : url).trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (/^[/#?]/.test(u)) return u;
  return '#';
}

export function wirePlayLinks(root: HTMLElement, navigate: (p: string) => void) {
  root.querySelectorAll<HTMLAnchorElement>('[data-play]').forEach(a => {
    const target = a.getAttribute('data-play') || '/login';
    a.href = target;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(target);
    });
  });
}

/** Which page the header is drawn on — the landing, or one of the article pages. */
export type SitePage = 'home' | 'news' | 'guides';

/**
 * Section links are bare `#id` on the landing (the browser scrolls) and `/#id`
 * everywhere else (the router opens the landing, then scrolls).
 */
function sectionHref(page: SitePage, id: string): string {
  return (page === 'home' ? '#' : '/#') + id;
}

export function siteHeader(page: SitePage): string {
  const s = (id: string) => sectionHref(page, id);
  const current = (p: SitePage) => (page === p ? ' aria-current="page"' : '');
  return `
  <a class="skip" href="#main">Skip to content</a>

  <header class="topbar">
    <div class="wrap">
      <a class="brand" href="${page === 'home' ? '#top' : '/'}" aria-label="Terraboma home">
        <img class="px" src="${A}/logo-wordmark-sm.png" alt="" width="450" height="88" />
        <span class="sr-only">Terraboma</span>
      </a>
      <nav class="navlinks" aria-label="Primary">
        <a href="${s('world')}">The world</a>
        <a href="${s('farm')}">Farming</a>
        <a href="${s('craft')}">Crafting</a>
        <a href="${s('night')}">Nights</a>
        <a href="${s('arena')}">Arena</a>
        <a href="/news"${current('news')}>News</a>
        <a href="/guides"${current('guides')}>Guides</a>
        <a href="${s('faq')}">FAQ</a>
      </nav>
      <a class="btn-board btn-board-sm" data-play="" href="#">Play</a>
    </div>
  </header>`;
}

/**
 * The operator identity block, under the footer wordmark and tagline.
 *
 * Every field is admin-editable and ships empty on purpose — the owner fills them
 * in the CMS. So this renders NOTHING until there is something to say, and each
 * line is built from the fields that are actually set: no half-empty row, no
 * separator with nothing on the other side of it. The brand `name` alone is not
 * enough to justify a block — it is already the wordmark two lines above; only a
 * real identity field (legal name, registry code, address, contact) opens it.
 */
function renderCompany(mount: HTMLElement) {
  cmsGet<{ legalName?: string; name?: string; regNumber?: string; vatNumber?: string; address?: string; country?: string; email?: string; phone?: string }>('/public/company').then(c => {
    if (!c) return;
    const t = (v: unknown) => String(v == null ? '' : v).trim();

    const legalName = t(c.legalName);
    const reg = t(c.regNumber);
    const vat = t(c.vatNumber);
    const address = t(c.address).split('\n').map(t).filter(Boolean).join(', ');
    const country = t(c.country);
    const email = t(c.email);
    const phone = t(c.phone);

    // The brand name only stands in for a missing legal name once something else
    // identifies the operator — otherwise it is just the logo repeated as text.
    const identified = !!(legalName || reg || vat || address || email || phone);
    if (!identified) return;

    const lines: string[] = [];
    const nameLine = legalName || t(c.name);
    if (nameLine) lines.push(nameLine);
    const ids = [reg && 'Reg. no. ' + reg, vat && 'VAT ' + vat].filter(Boolean) as string[];
    if (ids.length) lines.push(ids.join(' · '));
    const place = [address, country].filter(Boolean).join(', ');
    if (place) lines.push(place);
    if (phone) lines.push(phone);

    lines.forEach(l => mount.appendChild(el('span', null, l)));
    if (email) {
      const mail = el('a', null, email) as HTMLAnchorElement;
      mail.href = 'mailto:' + email;
      mount.appendChild(mail);
    }
  }).catch(() => {});
}

export function renderFooter(root: HTMLElement, page: SitePage) {
  const mount = root.querySelector('#site-footer');
  if (!mount) return;
  const s = (id: string) => sectionHref(page, id);

  const wrap = el('div', 'wrap');
  const cols = el('div', 'foot-cols');

  const brand = el('div', 'foot-brand');
  const logo = new Image();
  logo.src = `${A}/logo-wordmark-sm.png`;
  logo.alt = 'Terraboma';
  logo.className = 'px';
  logo.width = 450; logo.height = 88;
  brand.appendChild(logo);
  brand.appendChild(el('p', null, 'A cozy pixel-art farming world with teeth. Free to play in any browser, on a phone or a desktop.'));
  const company = el('div', 'foot-company');
  brand.appendChild(company);
  const socials = el('div', 'socials');
  brand.appendChild(socials);
  cols.appendChild(brand);

  renderCompany(company);

  const quick = el('div', 'foot-col');
  quick.appendChild(el('h4', null, 'Learn'));
  const quickNav = el('nav', 'foot-links');
  ([['Guide', '/guides'],
    ['FAQ', s('faq')],
    ['How it works', s('how')]] as const).forEach(([label, href]) => {
    const a = el('a', null, label) as HTMLAnchorElement;
    a.href = href;
    quickNav.appendChild(a);
  });
  quick.appendChild(quickNav);
  cols.appendChild(quick);

  wrap.appendChild(cols);

  // The accepted-payment badges sit above the copyright line — they are part of
  // the checkout promise, not decoration, so they render only for the methods an
  // operator has actually switched on (and the row disappears when none are).
  const payments = el('div', 'foot-payments');
  wrap.appendChild(payments);

  const legalRow = el('div', 'foot-legal');
  legalRow.appendChild(el('span', null, '© ' + new Date().getFullYear() + ' Terraboma'));
  wrap.appendChild(legalRow);

  mount.appendChild(wrap);

  function column(title: string, links: Array<{ label: string; url: string }>) {
    if (!links.length) return;
    const c = el('div', 'foot-col');
    c.appendChild(el('h4', null, title));
    links.forEach(l => {
      const a = el('a', null, l.label) as HTMLAnchorElement;
      a.href = safeHref(l.url);
      if (/^https?:\/\//.test(l.url) && l.url.indexOf(location.origin) !== 0) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      }
      c.appendChild(a);
    });
    cols.appendChild(c);
  }

  column('Explore', [
    { label: 'The world', url: s('world') },
    { label: 'Farming', url: s('farm') },
    { label: 'Crafting', url: s('craft') },
    { label: 'Nights', url: s('night') },
    { label: 'Lava Arena', url: s('arena') },
    { label: 'How it works', url: s('how') },
    { label: 'FAQ', url: s('faq') },
    { label: 'News', url: '/news' },
    { label: 'Guides', url: '/guides' },
  ]);

  cmsGet<{
    sections?: Array<{ title: string; links?: Array<{ label: string; url: string }> }>;
    social?: Array<{ platform: string; url: string; icon?: string }>;
    paymentMethods?: Array<{ slug: string; name: string; icon: string }>;
  }>('/footer').then(data => {
    (data.paymentMethods || []).forEach(m => {
      const img = new Image();
      img.src = /^[a-z0-9_-]+$/i.test(m.icon) ? '/payment-methods/' + m.icon + '.svg' : '';
      img.alt = m.name;
      img.title = m.name;
      img.loading = 'lazy';
      img.className = 'pay-badge';
      // A missing icon file leaves a broken-image box in the footer, which reads
      // worse than one fewer badge.
      img.addEventListener('error', () => img.remove());
      payments.appendChild(img);
    });
    (data.sections || []).forEach(sec => {
      column(sec.title, (sec.links || []).map(l => ({ label: l.label, url: l.url })));
    });
    (data.social || []).forEach(so => {
      const a = el('a', null) as HTMLAnchorElement;
      a.href = safeHref(so.url);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.title = so.platform;
      a.setAttribute('aria-label', so.platform);
      // The mark when the operator picked one (or the platform name matches a
      // shipped mark), the name in words when neither does — a footer must never
      // show an empty link.
      const src = resolveSocialIcon(so);
      if (src) {
        a.classList.add('social-icon');
        const mark = el('span', 'mark');
        mark.setAttribute('style', socialMaskStyle(src));
        a.appendChild(mark);
      } else {
        a.textContent = so.platform;
      }
      socials.appendChild(a);
    });
  }).catch(() => {
    column('Game', [{ label: 'Play free', url: '/' }, { label: 'Sign in', url: '/login' }]);
  });

  const FALLBACK_LEGAL = [
    { title: 'Privacy Policy', slug: 'privacy' },
    { title: 'Cookie Policy', slug: 'cookies' },
    { title: 'Terms of Service', slug: 'terms' },
  ];
  function legalColumn(pages: Array<{ title: string; slug: string }>) {
    column('Legal', pages.map(p => ({ label: p.title, url: '/' + p.slug })));
  }
  cmsGet<Array<{ title: string; slug: string }>>('/legal').then(pages => {
    legalColumn(pages?.length ? pages : FALLBACK_LEGAL);
  }).catch(() => { legalColumn(FALLBACK_LEGAL); });
}

export interface NewsItem {
  slug: string;
  title: string;
  summary?: string;
  imageUrl?: string;
  tag?: string;
  publishedAt?: string;
  content?: string;
}

export interface GuideItem {
  slug: string;
  title: string;
  summary?: string;
  imageUrl?: string;
  category?: string;
  content?: string;
}

function tileShell(href: string, item: { title: string; summary?: string; imageUrl?: string }) {
  const a = el('a', 'tile') as HTMLAnchorElement;
  a.href = href;
  if (item.imageUrl) {
    const img = new Image();
    img.className = 'tile-shot';
    img.src = mediaUrl(item.imageUrl);
    img.alt = '';
    img.loading = 'lazy';
    a.appendChild(img);
  }
  const body = el('div', 'tile-body');
  body.appendChild(el('h3', null, item.title));
  if (item.summary) body.appendChild(el('p', null, item.summary));
  const meta = el('div', 'tile-meta');
  body.appendChild(meta);
  a.appendChild(body);
  return { a, meta };
}

export const newsHref = (slug: string) => '/news/' + encodeURIComponent(slug);
export const guideHref = (slug: string) => '/guides/' + encodeURIComponent(slug);

export function newsTile(item: NewsItem): HTMLElement {
  const { a, meta } = tileShell(newsHref(item.slug), item);
  if (item.tag) meta.appendChild(el('span', 'pill', item.tag));
  meta.appendChild(el('span', null, fmtDate(item.publishedAt)));
  return a;
}

export function guideTile(item: GuideItem): HTMLElement {
  const { a, meta } = tileShell(guideHref(item.slug), item);
  a.setAttribute('data-category', item.category || '');
  if (item.category) meta.appendChild(el('span', 'pill', item.category));
  return a;
}
